import { LuEye, LuEyeOff } from "react-icons/lu";
import { MdOutlineAdd } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Input } from "../../components/InputTemplate";
import { IoQrCodeOutline } from "react-icons/io5";
import { RiFileExcel2Line } from "react-icons/ri";

// ------- TABULAR TABLE COMPONENTS ---------

export const TableInputField = ({
  placeholder,
  value,
  onChange,
  type,
  className,
}) => {
  return (
    <input
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      type={type}
      className={`bg-transparent outline-none ${className || ''}`}
    />
  );
};
export const TableActionButtons = ({ onPreview, onDelete, isPreviewOpen, onUpload, tableIndex, hasExcelData }) => {
  // Ensure boolean values are properly handled
  const excelDataClass = hasExcelData 
    ? "bg-green-50 border-green-500 text-green-700" 
    : "hover:bg-blue-50 hover:border-blue-300";
  
  const previewClass = isPreviewOpen ? "bg-blue-50 border-blue-300" : "";
  
  return (
    <div className="flex items-center gap-3">
      <button 
        className={`flex items-center gap-2 border p-1.5 px-4 rounded-md transition-colors ${excelDataClass || ''}`}
        onClick={() => onUpload && onUpload(tableIndex)}
        title={hasExcelData ? "Excel data loaded" : "Upload Excel file"}
      >
        <RiFileExcel2Line size={16}/>
        {hasExcelData ? "✓ Loaded" : "Upload"}
      </button>
      <button
        className={`flex items-center gap-2 border p-1.5 px-4 rounded-md ${previewClass || ''}`}
        onClick={onPreview}
      >
        {isPreviewOpen ? <LuEye /> : <LuEyeOff />}
        <span className="text-xs"> Preview</span>
      </button>

      <button
        onClick={onDelete}
        className="text-zinc-600 hover:text-red-400 border border-zinc-200 hover:border-red-200 p-1.5 rounded-lg"
      >
        <RiDeleteBin6Line />
      </button>
    </div>
  );
};

// ------TABLE PREVIEW

export const TabularTablePreview = ({ table }) => {
  const columns = table.columns || []; // Ensure columns is an array
  
  // Sort columns: "Ref" columns first (by sequence), then other columns (by sequence)
  const sortedColumns = [...columns].sort((a, b) => {
    const aSequence = Number(a?.sequence) || 0;
    const bSequence = Number(b?.sequence) || 0;
    const aIsRef = a?.format === "ref";
    const bIsRef = b?.format === "ref";
    
    // If one is Ref and the other isn't, Ref comes first
    if (aIsRef && !bIsRef) return -1;
    if (!aIsRef && bIsRef) return 1;
    
    // If both are Ref or both are not Ref, sort by sequence
    return aSequence - bSequence;
  });

  const maxLength =
    columns.length > 0
      ? Math.max(...columns.map((col) => (col.fixed ? col.fixed.length : 0)))
      : 0;
  // console.log("maxLength", maxLength)

  const normalizeFormat = (format) =>
    typeof format === "string" ? format.trim().toLowerCase() : "";

  const getCellPreview = (col, rowIndex) => {
    const format = normalizeFormat(col?.format);
    let fixed = Array.isArray(col?.fixed) ? col.fixed : [];

    // For qrcode with empty fixed, inherit from another qrcode or tour column (so all qrcode columns show preview)
    if (format === "qrcode" && fixed.length === 0) {
      const sourceCol =
        columns.find((c) => c !== col && normalizeFormat(c?.format) === "qrcode" && c?.fixed?.length > 0) ||
        columns.find((c) => c !== col && normalizeFormat(c?.format) === "tour" && c?.fixed?.length > 0);
      if (sourceCol) fixed = sourceCol.fixed;
    }

    const raw = fixed[rowIndex];

    // Only show preview values for: tour, qrcode, fixed value.
    if (!["tour", "qrcode", "fixed value"].includes(format)) return "";

    if (format === "qrcode") {
      return raw !== undefined && raw !== null && raw !== "" ? (
        <IoQrCodeOutline className="text-blue-500 text-xl" />
      ) : (
        ""
      );
    }

    if (raw === undefined || raw === null) return "";

    // Fixed Value should never auto-render Tour/Qrcode JSON payloads.
    if (format === "fixed value" && typeof raw === "string") {
      const s = raw.trim();
      if (s.startsWith("{")) {
        try {
          const parsed = JSON.parse(s);
          if (parsed?.checkpointName || parsed?.CheckpointName) return "";
        } catch {
          // fall through to render raw
        }
      }
      return raw;
    }

    // Tour values may be strings, objects, or stringified JSON.
    if (format === "tour") {
      if (typeof raw === "object") {
        return raw?.checkpointName || raw?.CheckpointName || "";
      }
      if (typeof raw === "string") {
        const s = raw.trim();
        if (s.startsWith("{")) {
          try {
            const parsed = JSON.parse(s);
            return parsed?.checkpointName || parsed?.CheckpointName || raw;
          } catch {
            return raw;
          }
        }
      }
      return raw;
    }

    return typeof raw === "string" ? raw : String(raw);
  };

  return (
    <div className=" bg-white shadow rounded-md mt-3 overflow-hidden">
      <div className="bg-[#00325d] text-white w-full text-xs text-blue-700 py-0.5 font-semibold px-4 capitalize">
        Preview
      </div>
      <div className="p-2">
        <div className="">
          {table?.name && (
            <p className="font-semibold mb-3 capitalize px-2 border-b pb-1">
              {table?.name}
            </p>
          )}

          <div className="">
            <table className="w-full text-sm text-left rtl:text-right capitalize border border-zinc-300">
              <thead className="text-xs text-gray-700 uppercase bg-[#00325d] text-white border border-zinc-300">
                <tr>
                  {sortedColumns.map((item, index) => {
                    return (
                      <th
                        scope="col"
                        className="px-6 py-3 border border-zinc-300"
                        key={index}
                        style={{
                          width: `${item?.width}%`,
                        }}
                      >
                        {item?.title}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: maxLength }).map((_, rowIndex) => (
                  <tr key={rowIndex} className="bg-white">
                    {sortedColumns.map((item, index) => {
                      return (
                        // <td
                        //   key={i + 1}
                        //   className="px-5 py-4 font-medium text-black whitespace-nowrap border border-zinc-300"
                        // >
                        //   {item?.format === "tour" &&
                        //   item?.fixed &&
                        //   item.fixed[rowIndex] !== undefined
                        //     ? item.fixed[rowIndex]?.checkpointName
                        //     : item?.fixed && item.fixed[rowIndex] !== undefined
                        //     ? item.fixed[rowIndex]
                        //     : ""}
                        // </td>
                        <td
                          key={index}
                          className="px-5 py-4 font-medium text-black whitespace-nowrap border border-zinc-300"
                        >
                          {getCellPreview(item, rowIndex)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TabularTableComponent = ({
  table,
  handleTableNameChange,
  handleChange,
  handleDeleteRow,
  handleAddColumn,
  handleDeleteTable,
  tableIndex,
  PreviewHandler,
  previewTableIds,
  UploadHandler,
  hasExcelData
}) => {
  console.log("table.name", table.name);
  return (
    <div className="bg-white shadow rounded-md overflow-hidden  my-5">
      <div className="bg-[#00325d] text-white w-full text-xs text-blue-700 py-0.5 font-semibold px-4 capitalize">
        Table
      </div>
      <div className="p-4 w-full flex flex-col gap-5 relative">
        <div className="flex justify-between">
          <div className="flex flex-col items-start">
            <input
              placeholder="Table Name"
              value={table.tableName}
              onChange={(e) =>
                handleTableNameChange(tableIndex, e.target.value)
              }
              className="bg-transparent outline-none text-2xl font-bold  text-zinc-600"
            />
            {table.tableName === "" && (
              <span className="border-[#00325d] border rounded-full px-5 text-xs text-[#00325d]">
                Editable
              </span>
            )}
          </div>
          <TableActionButtons
            onPreview={() => PreviewHandler(table.id)}
            onDelete={() => handleDeleteTable(tableIndex)}
            isPreviewOpen={previewTableIds.includes(table.id)}
            onUpload={UploadHandler}
            tableIndex={tableIndex}
            hasExcelData={hasExcelData}
          />
        </div>

        <div className="relative overflow-x-auto overflow-y-auto pb-8" style={{ maxHeight: '60vh', minHeight: '300px' }}>
          <table className="w-full text-sm text-left capitalize">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3">Sequence</th>
                <th className="px-6 py-3">Title</th>
                <th className="px-6 py-3">Width (%)</th>
                <th className="px-6 py-3">Specific Format</th>
                <th className="px-6 py-3">Border</th>
                <th className="px-6 py-3">Mandatory</th>
                <th className="px-6 py-3">
                  <button
                    onClick={() => handleAddColumn(tableIndex)}
                    className="p-1 px-2 border-2 border-blue-400 text-blue-500 rounded-lg flex items-center gap-1"
                  >
                    <MdOutlineAdd className="text-lg" />
                    <span>ADD</span>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {table.columns.map((column, columnIndex) => {
                const hideRow = column.type === "readOnly";
                const hasFixedValue = column.type === "fixedValue";
                return (
                  <tr class="bg-white border-b " >
                    <th
                      scope="row"
                      class="px-5 py-4 font-medium text-black whitespace-nowrap "
                    >
                      <Input
                        placeholder="Sequence"
                        className="w-20"
                        type="number"
                        value={column?.format === "ref" ? "1" : column.sequence}
                        onChange={(e) =>
                          handleChange(
                            tableIndex,
                            columnIndex,
                            "sequence",
                            e.target.value
                          )
                        }
                        disabled={column?.format === "ref"}
                        name="sequence"
                      />
                    </th>
                    <td class="px-5 py-4">
                      <Input
                        placeholder="Title"
                        type="text"
                        value={column.title}
                        onChange={(e) =>
                          handleChange(
                            tableIndex,
                            columnIndex,
                            "title",
                            e.target.value
                          )
                        }
                        name="title"
                      />
                    </td>

                    <td class="px-5 py-4">
                      <Input
                        className="w-20"
                        placeholder="width"
                        value={column.width}
                        onChange={(e) =>
                          e.target.value <= 100 &&
                          handleChange(
                            tableIndex,
                            columnIndex,
                            "width",
                            e.target.value
                          )
                        }
                        name="width"
                      />
                    </td>
                    <td class="px-5 py-4">
                      {/* <Input placeholder='Format ' /> */}
                      <select
                        className="px-2 border p-2 rounded-md capitalize"
                        value={column.format}
                        onChange={(e) =>
                          handleChange(
                            tableIndex,
                            columnIndex,
                            "format",
                            e.target.value
                          )
                        }
                        name="format"
                      >
                        {/* <option value="input">Text</option> */}
                        <option value="number">Number</option>
                        <option value="date">Date</option>
                        {/* <option value="time">Time</option> */}
                        <option value="textarea">Text</option>
                        <option value="fixed value">Fixed Value</option>
                        <option value="Yes/No">Yes/No</option>
                        <option value="photo Upload">Photo Upload</option>
                        <option value="photo Capture">Photo Capture</option>
                        <option value="f&c">F&C</option>
                        <option value="ref">Ref</option>
                        <option value={"time am/pm"}>Time - AM/PM</option>
                        <option value={"time military"}>Time - Military</option>
                        <option value={"checkbox"}>checkbox</option>
                        <option value={"e-sign"}>e-sign</option>
                        <option value={"tour"}>tour</option>
                        <option value={"qrcode"}>qrcode</option>
                      </select>
                    </td>
                    <td class="px-6 py-4">
                      <select
                        className="px-2 border p-2 rounded-md "
                        value={column.border}
                        onChange={(e) =>
                          handleChange(
                            tableIndex,
                            columnIndex,
                            "border",
                            e.target.value
                          )
                        }
                        name="border"
                      >
                        <option>no</option>
                        <option>yes</option>
                      </select>
                    </td>
                    <td class="px-6 py-4">
                      <select
                        className="px-2 border p-2 rounded-md "
                        value={column.mandatory}
                        onChange={(e) =>
                          handleChange(
                            tableIndex,
                            columnIndex,
                            "mandatory",
                            e.target.value
                          )
                        }
                        name="mandatory"
                      >
                        <option value={"false"}>no</option>
                        <option value={"true"}>yes</option>
                      </select>
                    </td>
                    <td className="">
                      <div className="flex justify-center items-center">
                        {/* {item?.id} */}
                        {column.type !== "readOnly" && (
                          <RiDeleteBin6Line
                            className="text-xl text-red-500 cursor-pointer"
                            onClick={() => handleDeleteRow(tableIndex, columnIndex)}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
